/**
 * AdminDirectorySharing — consent for being found outside your own module.
 *
 * A school or event admin authorises one of their people to appear in the
 * church admins' directory search. Nobody is discoverable until somebody says
 * so, and switching it off hides them again.
 *
 * Route: /admin/directory-sharing
 */
import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import api, { getErrorMessage } from '@/lib/api';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Loader2, Search, ShieldCheck, EyeOff } from 'lucide-react';

const MODULES = [
  { key: 'schools', label: 'Schools' },
  { key: 'events', label: 'Events' },
];

const FILTERS = [
  { key: 'all', label: 'Everyone' },
  { key: 'shared', label: 'Authorised' },
  { key: 'not_shared', label: 'Not authorised' },
];

const AdminDirectorySharing = () => {
  const qc = useQueryClient();
  const [module, setModule] = useState('schools');
  const [q, setQ] = useState('');
  const [only, setOnly] = useState('all');
  const [picked, setPicked] = useState(new Set());

  const { data, isLoading } = useQuery({
    queryKey: ['directory-sharing', module, q, only],
    queryFn: async () => (await api.get('/admin/directory-sharing', {
      params: { module, q, only, limit: 100 },
    })).data,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['directory-sharing'] });
    setPicked(new Set());
  };

  const one = useMutation({
    mutationFn: async ({ row, granted }) => (await api.put('/admin/directory-sharing', {
      source_collection: row.source_collection, record_id: row.record_id, granted,
    })).data,
    onSuccess: (_r, vars) => {
      toast.success(vars.granted
        ? `${vars.row.name} can now be found by church admins`
        : `${vars.row.name} is hidden from other modules again`);
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const bulk = useMutation({
    mutationFn: async (granted) => {
      const rows = (data?.people || []).filter((p) => picked.has(p.record_id));
      const byCollection = rows.reduce((acc, r) => {
        (acc[r.source_collection] = acc[r.source_collection] || []).push(r.record_id);
        return acc;
      }, {});
      const results = [];
      for (const [source_collection, record_ids] of Object.entries(byCollection)) {
        results.push((await api.post('/admin/directory-sharing/bulk', {
          source_collection, record_ids, granted,
        })).data);
      }
      return results;
    },
    onSuccess: (results) => {
      const n = results.reduce((t, r) => t + (r.updated || 0), 0);
      toast.success(`${n} ${n === 1 ? 'person' : 'people'} updated`);
      refresh();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const rows = data?.people || [];
  const toggle = (id) => {
    const next = new Set(picked);
    if (next.has(id)) next.delete(id); else next.add(id);
    setPicked(next);
  };

  return (
    <DashboardLayout title="Directory sharing">
      <div className="p-4 md:p-6 space-y-4 max-w-5xl" data-testid="directory-sharing-page">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Directory sharing</h1>
          <p className="text-sm text-slate-500 mt-1">
            Students and delegates are private to their own module. Authorise someone here and
            church admins can find them when adding an existing member — name, email and phone,
            nothing else. Switch it off and they disappear from that search again.
          </p>
        </div>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <CardTitle className="text-base">Who can be found</CardTitle>
                <CardDescription>
                  {data?.shared_total || 0} authorised in {module}
                </CardDescription>
              </div>
              <Tabs value={module} onValueChange={(v) => { setModule(v); setPicked(new Set()); }}>
                <TabsList data-testid="sharing-module-tabs">
                  {MODULES.map((m) => (
                    <TabsTrigger key={m.key} value={m.key} data-testid={`sharing-tab-${m.key}`}>
                      {m.label}
                    </TabsTrigger>
                  ))}
                </TabsList>
              </Tabs>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
                <Input value={q} onChange={(e) => setQ(e.target.value)}
                       placeholder="Search by name or email"
                       className="pl-9" data-testid="sharing-search" />
              </div>
              <div className="flex gap-1">
                {FILTERS.map((f) => (
                  <Button key={f.key} size="sm"
                          variant={only === f.key ? 'default' : 'outline'}
                          onClick={() => setOnly(f.key)}
                          data-testid={`sharing-filter-${f.key}`}>
                    {f.label}
                  </Button>
                ))}
              </div>
            </div>

            {picked.size > 0 && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 p-2.5"
                   data-testid="sharing-bulk-bar">
                <span className="text-xs text-slate-600 mr-auto">{picked.size} selected</span>
                <Button size="sm" disabled={bulk.isPending} onClick={() => bulk.mutate(true)}
                        data-testid="sharing-bulk-authorise">
                  <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Authorise
                </Button>
                <Button size="sm" variant="outline" disabled={bulk.isPending}
                        onClick={() => bulk.mutate(false)} data-testid="sharing-bulk-hide">
                  <EyeOff className="h-3.5 w-3.5 mr-1.5" /> Hide
                </Button>
              </div>
            )}

            {isLoading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : rows.length === 0 ? (
              <p className="py-8 text-center text-sm text-slate-500" data-testid="sharing-empty">
                Nobody matches. Search by name or email — the list shows this module's people.
              </p>
            ) : (
              <div className="divide-y" data-testid="sharing-list">
                {rows.map((row) => (
                  <div key={`${row.source_collection}-${row.record_id}`}
                       className="flex flex-wrap items-center gap-3 py-2.5"
                       data-testid={`sharing-row-${row.record_id}`}>
                    <Checkbox checked={picked.has(row.record_id)}
                              onCheckedChange={() => toggle(row.record_id)}
                              data-testid={`sharing-check-${row.record_id}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-800 truncate">{row.name}</p>
                      <p className="text-xs text-slate-500 truncate">
                        {row.source_label}{row.unit_name ? ` · ${row.unit_name}` : ''}
                        {row.email ? ` · ${row.email}` : ''}
                      </p>
                    </div>
                    {row.shared && (
                      <Badge variant="outline"
                             className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                        {row.granted_by_type === 'member' ? 'Authorised by them' : 'Authorised'}
                      </Badge>
                    )}
                    <Switch checked={row.shared} disabled={one.isPending}
                            onCheckedChange={(granted) => one.mutate({ row, granted })}
                            data-testid={`sharing-toggle-${row.record_id}`} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default AdminDirectorySharing;
